import React, { useEffect } from "react";
import { useTenant } from "../../../contexts/TenantContext";
import { useMediaQuery } from "../../../hooks/useMediaQuery";

export type ViewMode = "combined" | "map" | "properties";

interface ViewToggleProps {
  currentView: ViewMode;
  onViewChange: (view: ViewMode) => void;
}

export const ViewToggle: React.FC<ViewToggleProps> = ({
  currentView,
  onViewChange,
}) => {
  const { config } = useTenant();
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  if (!config) return null;

  useEffect(() => {
    // If on mobile/tablet and view is combined, switch to properties view
    if (!isDesktop && currentView === "combined") {
      onViewChange("properties");
    }
  }, [isDesktop, currentView, onViewChange]);

  const buttonStyle = (isActive: boolean) => ({
    backgroundColor: isActive ? config.branding.colors.primary : "transparent",
    color: isActive ? "#fff" : config.branding.colors.text,
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: isActive ? config.branding.colors.primary : "transparent",
    transition: "all 0.2s ease-in-out",
  });

  return (
    <div className="inline-flex rounded-lg gap-2">
      <button
        className="p-1.5 rounded-l-lg"
        style={buttonStyle(currentView === "properties")}
        onClick={() => onViewChange("properties")}
        title="List View"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 10h16M4 14h16M4 18h16"
          />
        </svg>
      </button>
      {isDesktop && (
        <button
          className="p-1.5"
          style={buttonStyle(currentView === "combined")}
          onClick={() => onViewChange("combined")}
          title="Split View"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
            />
          </svg>
        </button>
      )}
      <button
        className="p-1.5 rounded-r-lg"
        style={buttonStyle(currentView === "map")}
        onClick={() => onViewChange("map")}
        title="Map View"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
          />
        </svg>
      </button>
    </div>
  );
};

export default ViewToggle;
